import type {
  JsonObject,
  MapChunk,
  MapChunkRevision,
  MapMeta,
  MapOverview,
  MapTerrainPack,
} from './types'

interface CachedWorldRecord {
  scope: string
  meta: MapMeta
  overview: MapOverview
  markers: JsonObject[]
  projectionRevision: number
  markerRevision: number
  worldTimeUs: number
  chunkRevisions: MapChunkRevision[]
  terrain: MapTerrainPack
  savedAtMs: number
}

interface CachedChunkRecord {
  id: string
  scope: string
  key: string
  chunk: MapChunk
}

export interface PersistentMapState {
  world: CachedWorldRecord
  chunks: MapChunk[]
}

const DB_NAME = 'craft-imperium-map-v1'
const DB_VERSION = 1
const WORLD_STORE = 'worlds'
const CHUNK_STORE = 'chunks'
const MAX_MEMORY_WORLDS = 2
const memoryWorlds = new Map<string, PersistentMapState>()

function rememberMemory(scope: string, state: PersistentMapState): PersistentMapState {
  memoryWorlds.delete(scope)
  memoryWorlds.set(scope, state)
  while (memoryWorlds.size > MAX_MEMORY_WORLDS) {
    const oldest = memoryWorlds.keys().next()
    if (oldest.done) break
    memoryWorlds.delete(oldest.value)
  }
  return state
}

export function peekPersistentMap(scope: string): PersistentMapState | null {
  return memoryWorlds.get(scope) ?? null
}

export function retainPersistentMapScope(scope: string): void {
  for (const key of memoryWorlds.keys()) {
    if (key !== scope) memoryWorlds.delete(key)
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(WORLD_STORE)) {
        db.createObjectStore(WORLD_STORE, { keyPath: 'scope' })
      }
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, { keyPath: 'id' })
        store.createIndex('scope', 'scope', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('INDEXED_DB_OPEN_FAILED'))
  })
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('INDEXED_DB_REQUEST_FAILED'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('INDEXED_DB_TRANSACTION_FAILED'))
    transaction.onabort = () => reject(transaction.error ?? new Error('INDEXED_DB_TRANSACTION_ABORTED'))
  })
}

export function mapCacheScope(accountId: string, worldId: string): string {
  return `${accountId}|${worldId}`
}

export async function loadPersistentMap(scope: string): Promise<PersistentMapState | null> {
  const warm = peekPersistentMap(scope)
  if (warm) return warm
  if (!('indexedDB' in window)) return null
  try {
    const db = await openDatabase()
    const transaction = db.transaction([WORLD_STORE, CHUNK_STORE], 'readonly')
    const worldRequest = transaction.objectStore(WORLD_STORE).get(scope) as IDBRequest<
      CachedWorldRecord | undefined
    >
    const chunksRequest = transaction
      .objectStore(CHUNK_STORE)
      .index('scope')
      .getAll(scope) as IDBRequest<CachedChunkRecord[]>
    const [world, chunks] = await Promise.all([
      requestValue(worldRequest),
      requestValue(chunksRequest),
      transactionDone(transaction),
    ])
    db.close()
    if (!world) return null
    return rememberMemory(scope, { world, chunks: chunks.map((entry) => entry.chunk) })
  } catch {
    return null
  }
}

export async function replacePersistentMap(
  scope: string,
  world: Omit<CachedWorldRecord, 'scope' | 'savedAtMs'>,
  chunks: MapChunk[],
): Promise<void> {
  const cachedWorld = { ...world, scope, savedAtMs: Date.now() } satisfies CachedWorldRecord
  rememberMemory(scope, { world: cachedWorld, chunks: [...chunks] })
  if (!('indexedDB' in window)) return
  try {
    const db = await openDatabase()
    const readTransaction = db.transaction(CHUNK_STORE, 'readonly')
    const existingRequest = readTransaction
      .objectStore(CHUNK_STORE)
      .index('scope')
      .getAllKeys(scope) as IDBRequest<IDBValidKey[]>
    const [existing] = await Promise.all([
      requestValue(existingRequest),
      transactionDone(readTransaction),
    ])

    const transaction = db.transaction([WORLD_STORE, CHUNK_STORE], 'readwrite')
    const worldStore = transaction.objectStore(WORLD_STORE)
    const chunkStore = transaction.objectStore(CHUNK_STORE)
    worldStore.put(cachedWorld)
    for (const id of existing) chunkStore.delete(id)
    for (const chunk of chunks) {
      const key = `${String(chunk.chunk.x)}:${String(chunk.chunk.y)}`
      chunkStore.put({ id: `${scope}|${key}`, scope, key, chunk } satisfies CachedChunkRecord)
    }
    await transactionDone(transaction)
    db.close()
  } catch {
    // Persistent map cache is an optimization; in-memory rendering stays valid.
  }
}

export async function updatePersistentMap(
  scope: string,
  world: Omit<CachedWorldRecord, 'scope' | 'savedAtMs'>,
  changedChunks: MapChunk[],
): Promise<void> {
  const cachedWorld = { ...world, scope, savedAtMs: Date.now() } satisfies CachedWorldRecord
  const current = peekPersistentMap(scope)
  if (current) {
    const merged = new Map(
      current.chunks.map((chunk) => [`${String(chunk.chunk.x)}:${String(chunk.chunk.y)}`, chunk]),
    )
    for (const chunk of changedChunks) {
      const key = `${String(chunk.chunk.x)}:${String(chunk.chunk.y)}`
      if (chunk.objects.length === 0) merged.delete(key)
      else merged.set(key, chunk)
    }
    rememberMemory(scope, { world: cachedWorld, chunks: Array.from(merged.values()) })
  }
  if (!('indexedDB' in window)) return
  try {
    const db = await openDatabase()
    const transaction = db.transaction([WORLD_STORE, CHUNK_STORE], 'readwrite')
    transaction.objectStore(WORLD_STORE).put(cachedWorld)
    const chunkStore = transaction.objectStore(CHUNK_STORE)
    for (const chunk of changedChunks) {
      const key = `${String(chunk.chunk.x)}:${String(chunk.chunk.y)}`
      const id = `${scope}|${key}`
      if (chunk.objects.length === 0) chunkStore.delete(id)
      else chunkStore.put({ id, scope, key, chunk } satisfies CachedChunkRecord)
    }
    await transactionDone(transaction)
    db.close()
  } catch {
    // Persistent map cache is an optimization; in-memory rendering stays valid.
  }
}
